/**
 * sl-4wjh: Shared row conversion and aggregation utilities for feature tabs.
 *
 * DRY: Used by both AiChatTab and CapabilityFeatureTab to avoid
 * duplicating toBreakdownRows, computeTopModel, and computeTotalCalls.
 */
import type { AiUserUsageRow, WorkflowUsageRow } from './api';
import type { BreakdownRow } from './types';

const MICRO_PER_CREDIT = 10_000;

export type FetchResult = AiUserUsageRow[] | WorkflowUsageRow[];

function rowCredits(row: AiUserUsageRow | WorkflowUsageRow): number {
  return row.totalCostMicro / MICRO_PER_CREDIT;
}

export function userRowsToBreakdown(data: AiUserUsageRow[]): BreakdownRow[] {
  return data.map((row, index) => ({
    id: `${row.userId}:${row.model}:${index}`,
    name: row.userName,
    subtext: row.model,
    credits: rowCredits(row),
    calls: row.callCount,
    userId: row.userId,
    model: row.model,
  }));
}

export function workflowRowsToBreakdown(
  data: WorkflowUsageRow[]
): BreakdownRow[] {
  return data.map((row, index) => ({
    id: `${row.sourceId}:${row.model}:${index}`,
    name: row.workflowName,
    subtext: row.model,
    credits: rowCredits(row),
    calls: row.callCount,
    model: row.model,
  }));
}

export function toBreakdownRows(
  data: FetchResult,
  isWorkflow: boolean
): BreakdownRow[] {
  return isWorkflow
    ? workflowRowsToBreakdown(data as WorkflowUsageRow[])
    : userRowsToBreakdown(data as AiUserUsageRow[]);
}

export function computeTopModel(
  rows: BreakdownRow[]
): { model: string; credits: number } | null {
  if (rows.length === 0) return null;
  const byModel = new Map<string, number>();
  for (const row of rows) {
    const key = row.model ?? 'unknown';
    byModel.set(key, (byModel.get(key) ?? 0) + row.credits);
  }
  let best: { model: string; credits: number } | null = null;
  for (const [model, credits] of byModel) {
    if (!best || credits > best.credits) best = { model, credits };
  }
  return best;
}

export function computeTotalCalls(rows: BreakdownRow[]): number {
  return rows.reduce((sum, row) => sum + row.calls, 0);
}
